import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Fulcrum button (docs/fulcrum-redesign.md, phase 2 — Foundations "Buttons").
 *
 * - `primary`   indigo-600 fill. One per view.
 * - `secondary` / `outline` white with a slate-300 border (the spec's single
 *   "Secondary" look; both names kept for the ~650 existing call sites).
 * - `ghost`     transparent slate — toolbar / row-action / Cancel buttons.
 * - `link`      transparent indigo-700 text — the spec's tertiary "Ghost".
 * - `danger`    rose-600 fill (`destructive` is an alias).
 * - `success-soft` / `danger-soft` — small Approve / Reject row actions.
 *
 * Sizes: `sm` h-8 (rows, toolbars), `md` h-9 (default, page actions),
 * `lg` h-10, `icon` 36×36 square, `icon-sm` 32×32 square.
 * The base has no `gap` on purpose: hundreds of call sites already space
 * their icons with `mr-2`; new code passes `className="gap-1.5"`.
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'link'
  | 'danger'
  | 'destructive'
  | 'success-soft'
  | 'danger-soft';

export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const BORDERED = 'border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50 active:bg-slate-100';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 active:bg-indigo-800 disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none',
  secondary: BORDERED,
  outline: BORDERED,
  ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200',
  link: 'text-indigo-700 hover:bg-indigo-50 active:bg-indigo-100',
  danger:
    'bg-rose-600 text-white shadow-sm hover:bg-rose-700 active:bg-rose-800 disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none',
  destructive:
    'bg-rose-600 text-white shadow-sm hover:bg-rose-700 active:bg-rose-800 disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none',
  'success-soft': 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/25 hover:bg-emerald-100',
  'danger-soft': 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-600/25 hover:bg-rose-100',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-9 px-3.5 text-sm',
  lg: 'h-10 px-4 text-sm',
  icon: 'h-9 w-9 p-0',
  'icon-sm': 'h-8 w-8 p-0',
};

const SOLID_VARIANTS = new Set<ButtonVariant>(['primary', 'danger', 'destructive']);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', isLoading = false, className, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40',
        'disabled:pointer-events-none',
        // Solid variants carry an explicit slate disabled look; the rest fade.
        !SOLID_VARIANTS.has(variant) && 'disabled:opacity-50',
        VARIANT_CLASSES[variant] ?? VARIANT_CLASSES.primary,
        SIZE_CLASSES[size] ?? SIZE_CLASSES.md,
        className
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
      {children}
    </button>
  )
);

Button.displayName = 'Button';

export { Button };
export default Button;
